import { useEffect, useState } from "react";
import { Bot, Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { runAiTest } from "@/lib/api";
import { S } from "./summaryCopy";
import { highlightNames, competitorsInText } from "./reportUtils";
import { parseAiAnswer, cleanParagraphs } from "./aiAnswer";

const WRAP = "break-words [overflow-wrap:anywhere]";

// highlightNames returns plain strings + marker objects (reportUtils.js stays
// JSX-free); this is the one place they become <mark> elements.
function MarkedText({ text, businessName, competitorNames }) {
  const parts = highlightNames(text, businessName, competitorNames);
  if (!Array.isArray(parts)) return <>{parts}</>;
  return parts.map((p, i) =>
    typeof p === "string" ? <span key={i}>{p}</span> : <mark key={p.key} className={p.cls}>{p.text}</mark>
  );
}

// Delay before the automatic run: the reader has to actually stay on the page.
export const AUTO_RUN_DELAY_MS = 1500;

// The free report's live demo: one real ChatGPT answer, started by itself once
// the page has been visible for a moment (~$0.04, approved). The server allows
// exactly one paid query per report, caches the answer, caps the day and skips
// non-browser user agents; this component adds "at most one automatic attempt
// per mount" and never retries on its own after a failure or guard error.
export default function AiTestCard({ token, lang, initial, businessName, top3 }) {
  const C = S[lang].aiTest;
  const [test, setTest] = useState(initial || null);
  // pending = waiting for the automatic run | running | done | failed | busy
  const [phase, setPhase] = useState(initial?.result ? "done" : "pending");

  const start = async () => {
    if (phase === "running") return;
    setPhase("running");
    try {
      const t = await runAiTest(token);
      if (t?.result) setTest(t);
      setPhase(t?.result ? "done" : "failed");
    } catch (e) {
      // 429 = capacity / IP rate limit, 409 = another run holds the lock. Anything
      // else = provider trouble. None is rendered as a result: "0 %" would be a
      // false statement.
      const st = e?.response?.status;
      setPhase(st === 429 || st === 409 ? "busy" : "failed");
    }
  };

  useEffect(() => {
    if (phase !== "pending" || typeof document === "undefined") return undefined;
    let timer = null;
    const arm = () => { if (!timer) timer = setTimeout(start, AUTO_RUN_DELAY_MS); };
    const disarm = () => { if (timer) { clearTimeout(timer); timer = null; } };
    const onVis = () => (document.visibilityState === "visible" ? arm() : disarm());
    if (document.visibilityState === "visible") arm();
    document.addEventListener("visibilitychange", onVis);
    return () => { disarm(); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (!test?.available && !test?.result) return null;

  const result = test?.result;
  const competitorNames = (top3 || []).map((t) => t.label);
  const parsed = result ? parseAiAnswer(result.answer_excerpt) : null;
  const mark = (t) => <MarkedText text={t} businessName={businessName} competitorNames={competitorNames} />;
  // bl.sources (domains) is collected by the parser but deliberately not rendered.
  const groups = [];
  (parsed?.blocks || []).forEach((bl) => {
    const g = groups[groups.length - 1];
    if (bl.type !== "item") groups.push({ block: bl });
    else if (g?.list) g.list.push(bl);
    else groups.push({ list: [bl] });
  });
  const fallback = parsed && !parsed.ok ? cleanParagraphs(result.answer_excerpt) : [];
  const renderBlock = (bl, k) => {
    if (bl.type === "note") {
      return (
        <div key={k} className="min-w-0 rounded-lg bg-secondary/50 p-3 text-base text-muted-foreground">
          {bl.title && <p className={`mb-1 font-semibold text-foreground ${WRAP}`}>{mark(bl.title)}</p>}
          <ul className="min-w-0 space-y-1">
            {bl.lines.map((l, j) => <li key={j} className={WRAP}>{mark(l)}</li>)}
          </ul>
        </div>
      );
    }
    if (bl.type === "outro") return <p key={k} className={`text-sm italic text-muted-foreground ${WRAP}`}>{mark(bl.text)}</p>;
    if (bl.type === "intro") return <p key={k} className={`whitespace-pre-line text-base text-muted-foreground ${WRAP}`}>{mark(bl.text)}</p>;
    return <p key={k} className={`text-base text-muted-foreground ${WRAP}`}>{mark(bl.text)}</p>;
  };
  const named = result ? competitorsInText(result.answer_excerpt, top3) : [];
  // The harsher, data-backed title only once the real answer confirms it —
  // never while pending/running/failed, and never a guessed competitor count.
  const title = phase === "done" && result && !result.mentioned_in_answer && named.length > 0
    ? C.titleNotMentioned(named.length)
    : C.title;

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-2xl border bg-card p-5 shadow-sm sm:p-6" data-testid="ai-test-card">
      <h2 className="flex items-center gap-2 font-heading text-lg font-semibold">
        <Bot className="h-5 w-5 text-primary" /> {title}
      </h2>
      {phase !== "pending" && phase !== "running" && (
        <p className="mt-1 text-base font-medium" data-testid="ai-question">„{test.query}“</p>
      )}

      {(phase === "pending" || phase === "running") && (
        <div className="mt-3" role="status" data-testid="ai-skeleton">
          <p className="flex items-start gap-2 text-base text-muted-foreground">
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" /> {C.running(test.query)}
          </p>
          <div className="mt-3 space-y-2" aria-hidden="true">
            <div className="h-3 w-11/12 animate-pulse rounded bg-secondary" />
            <div className="h-3 w-4/5 animate-pulse rounded bg-secondary" />
            <div className="h-3 w-2/3 animate-pulse rounded bg-secondary" />
          </div>
        </div>
      )}
      {(phase === "failed" || phase === "busy") && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-base text-amber-900" role="alert">
          {phase === "busy" ? C.busy : C.failed}
        </div>
      )}
      {(phase === "failed" || phase === "busy") && (
        <Button className="mt-3" variant="secondary" onClick={start} data-testid="ai-test-start">{C.button}</Button>
      )}

      {phase === "done" && result && (
        <div className="mt-3 space-y-3">
          <div
            className={`flex items-start gap-2 rounded-lg border p-3 text-base ${
              result.mentioned_in_answer
                ? "border-green-200 bg-green-50 text-green-900"
                : "border-amber-200 bg-amber-50 text-amber-900"
            }`}
          >
            {result.mentioned_in_answer
              ? <Check className="mt-0.5 h-4 w-4 shrink-0" />
              : <X className="mt-0.5 h-4 w-4 shrink-0" />}
            <div className="min-w-0">
              <p className="font-medium">{result.mentioned_in_answer ? C.mentioned : C.notMentioned}</p>
              {!result.mentioned_in_answer && named.length > 0 && (
                <p className="mt-0.5">{C.namedInstead(named.join(", "))}</p>
              )}
            </div>
          </div>
          <div className="min-w-0 space-y-3" data-testid="ai-answer">
            {(parsed.ok || fallback.length > 0) && (
            <div className="min-w-0 space-y-3" data-testid="ai-answer-structured">
              {parsed.ok ? (
                groups.map((g, k) => {
                  if (g.list) {
                    return (
                      <div key={k} role="list" aria-label={C.answerHeading} className="min-w-0 space-y-3">
                        {g.list.map((bl) => (
                          <div key={bl.n} role="listitem" className="flex min-w-0 gap-3 rounded-xl border bg-card p-4" data-testid="ai-item">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-secondary text-sm font-semibold">{bl.n}</span>
                            <div className="min-w-0 flex-1">
                              <p className={`font-semibold ${WRAP}`}>{mark(bl.title)}</p>
                              {bl.body.length === 1 && <p className={`mt-1 text-base text-muted-foreground ${WRAP}`}>{mark(bl.body[0])}</p>}
                              {bl.body.length > 1 && (
                                <ul className="mt-1 min-w-0 space-y-0.5 text-base text-muted-foreground">
                                  {bl.body.map((l, j) => <li key={j} className={WRAP}>{mark(l)}</li>)}
                                </ul>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  const bl = g.block;
                  return renderBlock(bl, k);
                })
              ) : (
                fallback.map((p, i) => (
                  <p key={i} className={`whitespace-pre-line text-base text-muted-foreground ${WRAP}`}>{mark(p)}</p>
                ))
              )}
            </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{C.note}</p>
        </div>
      )}
    </div>
  );
}
